// 1-bit icons for the Nono editor. A bitmap is { w, h, bits } with bits a Uint8Array of 0/1 (1 = ink),
// row-major. The library (public/onebit-icons.json, built by scripts/buildOneBitIcons.py) stores them packed.

let library = null;

export const decodeOneBit = ([w, h, data, name, source]) => {
  const raw = atob(data);
  const bits = new Uint8Array(w * h);
  for (let i = 0; i < bits.length; i++) bits[i] = (raw.charCodeAt(i >> 3) >> (7 - (i & 7))) & 1;
  return { w, h, bits, name, source };
};

export const loadOneBitIcons = () => {
  if (!library) {
    library = fetch(`${process.env.PUBLIC_URL || ''}/onebit-icons.json`)
      .then(r => { if (!r.ok) throw new Error('1-bittisten kuvien lataus epäonnistui'); return r.json(); })
      .then(json => json.icons.map(decodeOneBit))
      .catch(e => { library = null; throw e; });
  }
  return library;
};

export const shuffled = (list) => {
  const out = [...list];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

export const deleteRow = (b, row) => {
  const bits = new Uint8Array(b.bits.length - b.w);
  bits.set(b.bits.subarray(0, row * b.w));
  bits.set(b.bits.subarray((row + 1) * b.w), row * b.w);
  return { ...b, h: b.h - 1, bits };
};

export const deleteColumn = (b, col) => {
  const bits = new Uint8Array((b.w - 1) * b.h);
  for (let r = 0; r < b.h; r++) {
    for (let c = 0, k = 0; c < b.w; c++) if (c !== col) bits[r * (b.w - 1) + k++] = b.bits[r * b.w + c];
  }
  return { ...b, w: b.w - 1, bits };
};

export const flipped = (b, horizontal) => {
  const bits = new Uint8Array(b.bits.length);
  for (let r = 0; r < b.h; r++) {
    for (let c = 0; c < b.w; c++) {
      bits[r * b.w + c] = horizontal ? b.bits[r * b.w + (b.w - 1 - c)] : b.bits[(b.h - 1 - r) * b.w + c];
    }
  }
  return { ...b, bits };
};

// Turns the bitmap 90° clockwise (or counter-clockwise), swapping width and height.
export const rotated = (b, clockwise = true) => {
  const bits = new Uint8Array(b.bits.length);
  for (let y = 0; y < b.w; y++) {
    for (let x = 0; x < b.h; x++) {
      bits[y * b.h + x] = clockwise ? b.bits[(b.h - 1 - x) * b.w + y] : b.bits[x * b.w + (b.w - 1 - y)];
    }
  }
  return { ...b, w: b.h, h: b.w, bits };
};

// Fills every area the ink encloses (outline icons become solid shapes).
export const filledShape = (b) => {
  const outside = new Uint8Array(b.bits.length);
  const stack = [];
  const push = (i) => { if (!outside[i] && !b.bits[i]) { outside[i] = 1; stack.push(i); } };
  for (let c = 0; c < b.w; c++) { push(c); push((b.h - 1) * b.w + c); }
  for (let r = 0; r < b.h; r++) { push(r * b.w); push(r * b.w + b.w - 1); }
  while (stack.length) {
    const i = stack.pop();
    const c = i % b.w;
    if (c > 0) push(i - 1);
    if (c < b.w - 1) push(i + 1);
    if (i >= b.w) push(i - b.w);
    if (i < b.w * (b.h - 1)) push(i + b.w);
  }
  return { ...b, bits: b.bits.map((v, i) => (outside[i] ? 0 : 1)) };
};

// Nearest-neighbour resample to tw×th: output pixel i takes the source pixel under its centre.
export const resampled = (b, tw, th) => {
  if (tw === b.w && th === b.h) return b;
  const bits = new Uint8Array(tw * th);
  for (let y = 0; y < th; y++) {
    const sy = Math.min(b.h - 1, Math.floor(((y + 0.5) * b.h) / th));
    for (let x = 0; x < tw; x++) bits[y * tw + x] = b.bits[sy * b.w + Math.min(b.w - 1, Math.floor(((x + 0.5) * b.w) / tw))];
  }
  return { ...b, w: tw, h: th, bits };
};

// Puts a bitmap on a width×height grid with its top-left corner at (x, y); anything outside is cut off.
export const placedCells = (b, width, height, x, y, invert = false) => {
  const cells = new Array(width * height).fill(0);
  for (let r = 0; r < b.h; r++) {
    for (let c = 0; c < b.w; c++) {
      const gr = r + y, gc = c + x;
      if (gr < 0 || gc < 0 || gr >= height || gc >= width) continue;
      cells[gr * width + gc] = invert ? 1 - b.bits[r * b.w + c] : b.bits[r * b.w + c];
    }
  }
  return cells;
};

// Removes grid rows or columns (sorted-independent list of indexes).
export const withoutLines = (grid, kind, index) => {
  const { width: w, height: h, cells } = grid;
  if (kind === 'row') {
    if (h <= 1) return grid;
    return { ...grid, height: h - 1, cells: [...cells.slice(0, index * w), ...cells.slice((index + 1) * w)] };
  }
  if (w <= 1) return grid;
  const out = [];
  for (let r = 0; r < h; r++) for (let c = 0; c < w; c++) if (c !== index) out.push(cells[r * w + c]);
  return { ...grid, width: w - 1, cells: out };
};

// The packed base64 form the library uses (rows after each other, 8 pixels per byte, first pixel in the top bit).
export const packBits = (bits) => {
  const bytes = new Uint8Array(Math.ceil(bits.length / 8));
  for (let i = 0; i < bits.length; i++) if (bits[i]) bytes[i >> 3] |= 0x80 >> (i & 7);
  return btoa(String.fromCharCode(...bytes));
};
